import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthorizedAppsService, type AuthorizedApp } from '../services/authorizedAppsService';
import { useToast } from '@/shared/hooks/use-toast';
import { logger } from '@/infrastructure/lib/logger';
import { useTranslation } from 'react-i18next';

export function useAuthorizedAppsQuery(workspaceId: string) {
  return useQuery<AuthorizedApp[]>({
    queryKey: ['authorized-apps', workspaceId],
    queryFn: () => AuthorizedAppsService.listByWorkspace(workspaceId),
    enabled: !!workspaceId,
    // Near real-time freshness: refetch every minute to pick up server-side token refreshes.
    // Combined with a client-side tick in the UI, On → Off transitions surface without reload.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

export function useRevokeAuthorizedApp(workspaceId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: string) => AuthorizedAppsService.revoke(workspaceId, id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['authorized-apps', workspaceId] });
      toast({ title: t('workspaces.authorizedApps.revoked') });
    },
    onError: (error: Error) => {
      logger.error('Failed to revoke authorized app', { error: error?.message });
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: t('workspaces.authorizedApps.revokeError'),
      });
    },
  });
}
